import { HttpEndpoint } from '@/api/httpEndpoints';
import { delay } from '@shared/utils/async';
import { getFakeSeating } from './seating.fake';
import { FlightProduct, SeatAvailability } from '@/api/Cart/cart.interfaces';
import { logStatsigSeatingAvailabilityStatus } from '@/tracking/statsig/general.functions';
import { parseEnumValue } from '@shared/utils/enum';

export class SeatingEndpoint extends HttpEndpoint {
    constructor(applicationUrl: string) {
        super(applicationUrl);
    }
    public async SyncSeats(product: FlightProduct): Promise<FlightProduct> {
        return await this.post<FlightProduct>(`mvc/Booking/SyncCartWithGordian`, {
            gordianInfo: product.Gordian,
            clearCart: false,
        });
    }
    public async ClearSeats(product: FlightProduct): Promise<FlightProduct> {
        return await this.post<FlightProduct>(`mvc/Booking/SyncCartWithGordian`, {
            gordianInfo: product.Gordian,
            clearCart: true,
        });
    }

    public async CheckAvailability(
        tripId: string,
        searchId: string,
        requestedCarriers: string[]
    ): Promise<SeatAvailability> {
        const availability = await this.checkAvailability(tripId, searchId);

        logStatsigSeatingAvailabilityStatus(availability, requestedCarriers);

        return availability;
    }

    private async checkAvailability(
        tripId: string,
        searchId: string
    ): Promise<SeatAvailability> {
        const TIMEOUT_IN_SECONDS = 20;
        const UPDATE_INTERVAL_MS = 2000;
        const MAX_ATTEMPTS = (TIMEOUT_IN_SECONDS * 1000) / UPDATE_INTERVAL_MS;

        const checkAvailabilityCall = async (): Promise<SeatAvailability> => {
            const raw = await this.get<any>(
                this.buildPathWithQuery('mvc/seatmap/getGordianSeatResultStatus', {
                    tripId,
                    searchId,
                })
            );

            const status =
                typeof raw === 'string'
                    ? raw
                    : (raw?.data?.status ??
                      raw?.result?.status ??
                      (typeof raw?.status === 'string' ? raw.status : undefined) ??
                      SeatAvailability.Unknown);

            return parseEnumValue(status, SeatAvailability) ?? SeatAvailability.Unknown;
        };

        const getAvailability = async (attemptsLeft: number) => {
            while (attemptsLeft > 0) {
                try {
                    const availability = await checkAvailabilityCall();
                    if (availability !== SeatAvailability.InProgress) {
                        return availability;
                    }
                } catch {
                    return SeatAvailability.Unknown;
                }

                attemptsLeft--;
                await delay(UPDATE_INTERVAL_MS);
            }

            return SeatAvailability.Unknown;
        };

        return getAvailability(MAX_ATTEMPTS);
    }
}

export class FakeSeatingEndpoint {
    public async SyncSeats(product: FlightProduct): Promise<FlightProduct> {
        await delay(2000);
        return getFakeSeating(product);
    }

    public async ClearSeats(product: FlightProduct): Promise<FlightProduct> {
        await delay(2000);
        return {
            ...product,
            Pax: product.Pax.map((pax) => ({
                ...pax,
                FirstName: null,
                LastName: null,
                SelectedSeats: null,
            })),
        };
    }

    public async CheckAvailability(): Promise<SeatAvailability> {
        const randomNumber = parseInt((Math.random() * 10).toString(), 10);
        await delay(1000 * randomNumber);
        return SeatAvailability.Available;
    }
}
